'use client';

import React, { useRef, useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Title, Text } from '@/components/ui/Headings';

interface Guide {
  id: number;
  type: string;
  slug: string;
  title: Record<string, string>;
  excerpt: Record<string, string>;
  heroImage: string;
  thumbnailImage: string | null;
  isFeatured: boolean;
}

interface GuidesCarouselProps {
  guides: Guide[];
  locale: string;
  className?: string;
}

export default function GuidesCarousel({ guides, locale, className = '' }: GuidesCarouselProps) {
  const { t } = useTranslation();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Create infinite loop by duplicating items
  const extendedGuides = [...guides, ...guides, ...guides];

  // Initialize scroll position to middle set on mount
  useEffect(() => {
    if (carouselRef.current && guides.length > 0) {
      const container = carouselRef.current;
      const firstCard = container.querySelector('div[class*="flex-shrink-0"]') as HTMLElement;

      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = 24;
        const scrollAmount = (cardWidth + gap) * guides.length;

        // Start at the middle set (second copy)
        container.scrollLeft = scrollAmount;
      }
    }
  }, [guides.length]);

  // Handle infinite loop
  useEffect(() => {
    const container = carouselRef.current;
    if (!container || guides.length === 0) return;

    const handleScroll = () => {
      if (isTransitioning) return;

      const firstCard = container.querySelector('div[class*="flex-shrink-0"]') as HTMLElement;
      if (!firstCard) return;

      const cardWidth = firstCard.offsetWidth;
      const gap = 24;
      const scrollAmount = (cardWidth + gap) * guides.length;
      const maxScroll = container.scrollWidth - container.clientWidth;

      // If scrolled to or past the end, jump to the beginning of second set
      if (container.scrollLeft >= maxScroll - 10) {
        setIsTransitioning(true);
        container.style.scrollBehavior = 'auto';
        container.scrollLeft = scrollAmount;
        container.style.scrollBehavior = 'smooth';
        setTimeout(() => setIsTransitioning(false), 50);
      }
      // If scrolled to or past the beginning, jump to the end of second set
      else if (container.scrollLeft <= 10) {
        setIsTransitioning(true);
        container.style.scrollBehavior = 'auto';
        container.scrollLeft = scrollAmount;
        container.style.scrollBehavior = 'smooth';
        setTimeout(() => setIsTransitioning(false), 50);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [guides.length, isTransitioning]);

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (carouselRef.current && !isTransitioning) {
      const container = carouselRef.current;
      const firstCard = container.querySelector('div[class*="flex-shrink-0"]') as HTMLElement;

      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = 24; // gap-6 = 24px
        const scrollAmount = cardWidth + gap;

        // Scroll by exactly one card width
        const newScrollLeft = direction === 'left'
          ? container.scrollLeft - scrollAmount
          : container.scrollLeft + scrollAmount;

        container.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth'
        });
      }
    }
  };

  // Only show navigation buttons if there are more than 3 items
  const showNavigation = guides.length > 3;

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      {/* Left Navigation Button */}
      {showNavigation ? (
        <button
          onClick={() => scrollCarousel('left')}
          className="hidden md:flex flex-shrink-0 bg-primary-600 hover:bg-primary-700 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300"
          aria-label="Scroll left"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      ) : (
        <div className="hidden md:block" />
      )}

      {/* Carousel Container */}
      <div className="flex-1 overflow-hidden">
        <div
          ref={carouselRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4 snap-x snap-mandatory"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {extendedGuides.map((guide, index) => {
            const title = guide.title[locale] || guide.title.en || '';
            const excerpt = guide.excerpt[locale] || guide.excerpt.en || '';

            return (
              <div
                key={`${guide.id}-${index}`}
                className="flex-shrink-0 w-[calc(100%-3rem)] sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)] snap-start snap-always"
                style={{ scrollSnapAlign: 'start' }}
              >
                <Link
                  href={`/${locale}/travel-guides/${guide.slug}`}
                  className="group block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <div className="relative h-56 overflow-hidden">
                    <Image
                      src={guide.heroImage}
                      alt={title}
                      fill
                      className="object-cover transition-transform duration-300 group-hover:scale-105"
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-2 text-white/90 mb-2">
                        <MapPin className="h-3.5 w-3.5" />
                        <span className="text-xs font-medium capitalize">{guide.type}</span>
                      </div>
                      <Title size="sm" className="text-white mb-0">
                        {title}
                      </Title>
                    </div>
                  </div>
                  <div className="p-5">
                    <Text variant="body" className="text-gray-600 line-clamp-2 mb-3">
                      {excerpt}
                    </Text>
                    <div className="flex items-center text-primary-600 text-sm font-medium group-hover:gap-2 transition-all duration-300">
                      <span>{t('travelGuides.index.viewGuide')}</span>
                      <span className="inline-block transform group-hover:translate-x-1 transition-transform duration-300">→</span>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Navigation Button */}
      {showNavigation ? (
        <button
          onClick={() => scrollCarousel('right')}
          className="hidden md:flex flex-shrink-0 bg-primary-600 hover:bg-primary-700 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300"
          aria-label="Scroll right"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      ) : (
        <div className="hidden md:block" />
      )}
    </div>
  );
}
